/* ═══ CRM IDENTITY RESOLUTION ═══
   The ONE place that decides "is this the same person / the same company".

   Before this module there were ~8 sites that each re-implemented matching with
   a bare `.toLowerCase()` — half of them without `.trim()` — so " Nike " and
   "Nike, Inc." each orphaned a quote from the account it belonged to. Anything
   that needs to resolve a person or a company against the CRM imports from here
   and nowhere else.

   Two rules that shape every function below:

   1. **Email/domain first, name second.** A name is a weak identifier: the live
      CRM holds 58 same-name pairs that are genuinely two different addresses for
      one person, and plenty of real distinct people share a name. An email is
      the only thing we treat as proof.

   2. **Fuzzy is advisory-only.** The token-subset tier ("Jane Smith" matching
      "Jane Smithson") is useful for *suggesting* a link to an operator who can
      look at it, and actively dangerous on a persistence path where nobody
      reviews the result. `resolveContact`/`resolveClient` — the persistence
      helpers — never reach that tier. Only `matchContactsToCRM`, which feeds the
      review UI, does, and it labels the result `confidence: "low"`. */

/* ── Normalization ───────────────────────────────────────────────────────── */

// Legal-entity suffixes stripped when comparing company names, so "Nike",
// "Nike Inc." and "Nike, LLC" collapse to one key. Superset of the list in
// `mkClientCode` (utils.js) — that one only needs enough to build a 6-char code,
// this one is the identity key.
const LEGAL_SUFFIXES = new Set([
  "llc", "l.l.c", "inc", "incorporated", "co", "company", "corp", "corporation",
  "ltd", "limited", "lp", "llp", "plc", "pllc", "gmbh", "ag", "sa", "sas",
  "srl", "bv", "nv", "oy", "ab", "as", "pty", "dac",
]);

/** Trim, collapse internal whitespace, case-fold. The base for every key. */
export function normalizeName(raw) {
  return String(raw ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/** Company identity key: `normalizeName` plus punctuation and TRAILING legal
 *  suffixes removed. Trailing only — filtering suffix words anywhere collapsed
 *  distinct names ("The Company Store" → "the store"; "co" and "as" are real
 *  words mid-name) (Codex review 2026-08-26, F6). Falls back to the
 *  punctuation-stripped name when a company is *only* suffixes (e.g. "Co."),
 *  so the key is never empty for a real name. */
export function normalizeCompanyName(raw) {
  const base = normalizeName(raw).replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
  if (!base) return "";
  const words = base.split(" ").filter(Boolean);
  const stripped = [...words];
  while (stripped.length > 1 && LEGAL_SUFFIXES.has(stripped[stripped.length - 1])) stripped.pop();
  return (LEGAL_SUFFIXES.has(stripped[0]) && stripped.length === 1 ? words : stripped).join(" ");
}

/** Trim + lowercase. Also unwraps the `name <mailto:addr>` shape that the 2026-03
 *  CRM import left in some records, and drops surrounding angle brackets. */
export function normalizeEmail(raw) {
  let s = String(raw ?? "").trim().toLowerCase();
  const mailto = s.match(/<mailto:([^>]+)>/);
  if (mailto) s = mailto[1];
  s = s.replace(/^<|>$/g, "").trim();
  return s.includes("@") ? s : "";
}

/** Reduce any url/domain/email shape to a bare host:
 *  "https://Acme.com/about", "www.acme.com", "x@acme.com" → "acme.com". */
export function normalizeDomain(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  if (s.includes("@")) return s.split("@").pop().split(/[/?#]/)[0].replace(/^www\./, "");
  return s
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split(/[/?#]/)[0]
    .trim();
}

/** The domain of an email, or "" — the durable org key for `client.orgDomain`. */
export const emailDomain = (raw) => {
  const e = normalizeEmail(raw);
  return e ? e.split("@")[1] || "" : "";
};

/* ── Internal / non-client addresses ─────────────────────────────────────── */

// Our own domains + the individually-listed internal addresses. Anything here is
// staff, never a CRM contact. `CRM_INTERNAL_EMAILS` lives in constants.js and was
// declared-but-unused until this module; it is passed in rather than imported so
// this file stays dependency-free and trivially testable.
export const INTERNAL_DOMAINS = ["newyorkcapture.com", "nycap3d.com", "lenscloud3d.com"];

// Free/consumer mail hosts. A shared domain here means nothing about employer, so
// domain-tier matching must never fire on them.
export const GENERIC_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "ymail.com", "hotmail.com",
  "outlook.com", "live.com", "msn.com", "aol.com", "icloud.com", "me.com",
  "mac.com", "proton.me", "protonmail.com", "gmx.com", "mail.com", "zoho.com",
  "comcast.net", "verizon.net", "att.net", "sbcglobal.net", "fastmail.com",
]);

/** True when an address belongs to us (our domain, or an explicit internal
 *  address). Extra addresses are supplied by the caller — pass
 *  `CRM_INTERNAL_EMAILS` from constants.js. */
export function isInternalEmail(raw, extraInternal = []) {
  const e = normalizeEmail(raw);
  if (!e) return false;
  if (extraInternal.some((x) => normalizeEmail(x) === e)) return true;
  return INTERNAL_DOMAINS.includes(emailDomain(e));
}

/** A domain worth matching on: present, not ours, not a free mail host. */
export function isOrgDomain(raw) {
  const d = normalizeDomain(raw);
  return !!d && d.includes(".") && !INTERNAL_DOMAINS.includes(d) && !GENERIC_EMAIL_DOMAINS.has(d);
}

/* ── Record-shape readers ────────────────────────────────────────────────── */

/* Contacts store `emails[]`, clients store a single `email` string
   (data/crm.md — a real schema split, not drift to fix here). Both readers
   accept either shape so callers never branch on record type. */

/** Every normalized address on a record, deduped. */
export function recordEmails(record) {
  const out = new Set();
  for (const e of record?.emails || []) {
    const n = normalizeEmail(e);
    if (n) out.add(n);
  }
  const single = normalizeEmail(record?.email);
  if (single) out.add(single);
  return [...out];
}

/** Every org domain a record is known by: explicit `orgDomain` + its addresses'
 *  domains (generic hosts excluded). */
export function recordDomains(record) {
  const out = new Set();
  const org = normalizeDomain(record?.orgDomain);
  if (isOrgDomain(org)) out.add(org);
  for (const e of recordEmails(record)) {
    const d = emailDomain(e);
    if (isOrgDomain(d)) out.add(d);
  }
  return [...out];
}

/* ── Resolution (persistence-safe) ───────────────────────────────────────── */

/* `resolve*` return `{ record, tier }` — never a bare record — so a caller can
   see *why* something matched and refuse a weak tier. Tiers, strongest first:
     "email"  — an address in common. Proof.
     "domain" — a shared org domain (clients only; a person sharing an employer
                is not the same person).
     "name"   — exact normalized name. Good enough to link, weak enough to log.
   No fuzzy tier exists here by design; see the header. */

/** Resolve a person against `contacts`. Email first, then exact name. */
export function resolveContact({ email, name } = {}, contacts = []) {
  const list = contacts || [];
  const e = normalizeEmail(email);
  if (e) {
    const hit = list.find((c) => recordEmails(c).includes(e));
    if (hit) return { record: hit, tier: "email" };
  }
  const n = normalizeName(name);
  if (n) {
    // Refuse to guess between same-name records: the live CRM holds 58 name-twin
    // pairs, and picking array order silently links the wrong one (Codex F7).
    // Ambiguity is a null result — the operator's picker is the tie-breaker.
    const hits = list.filter((c) => normalizeName(c.name) === n);
    if (hits.length === 1) return { record: hits[0], tier: "name" };
  }
  return { record: null, tier: null };
}

/** Resolve a company against `clients`. Email, then org domain, then
 *  legal-suffix-stripped name — so " Nike, Inc. " finds "Nike" (CD1). */
export function resolveClient({ name, domain, email } = {}, clients = []) {
  const list = clients || [];

  const e = normalizeEmail(email);
  if (e) {
    const hit = list.find((c) => recordEmails(c).includes(e));
    if (hit) return { record: hit, tier: "email" };
  }

  const d = normalizeDomain(domain) || emailDomain(email);
  if (isOrgDomain(d)) {
    const hit = list.find((c) => recordDomains(c).includes(d));
    if (hit) return { record: hit, tier: "domain" };
  }

  const n = normalizeCompanyName(name);
  if (n) {
    // Same ambiguity refusal as resolveContact — one hit or none.
    const hits = list.filter((c) => normalizeCompanyName(c.name) === n);
    if (hits.length === 1) return { record: hits[0], tier: "name" };
  }
  return { record: null, tier: null };
}

/* ── Review-UI matcher (fuzzy allowed) ───────────────────────────────────── */

/* Promoted from builderHelpers.js. Behaviour preserved except that it now uses
   the normalizers above (so " Nike " and mailto-wrapped addresses resolve), skips
   generic mail hosts on the domain tier, and filters internal addresses through
   `isInternalEmail`. Output feeds `buildContactMatches` → the Link/New/No-Link
   panel, where a human sees every row — which is the only reason the
   `confidence: "low"` token-subset tier is permitted at all. */

/**
 * @param extractedContacts  AI-extracted people: {name, email, url, role, source}
 * @param contacts           CRM contacts
 * @param clients            CRM clients
 * @param opts.internalEmails  extra internal addresses (pass CRM_INTERNAL_EMAILS)
 * @returns [{ extracted, match, _originalMatch, confidence }]
 */
export function matchContactsToCRM(extractedContacts, contacts, clients, opts = {}) {
  const internalEmails = opts.internalEmails || [];

  return (extractedContacts || [])
    .filter((ec) => !isInternalEmail(ec?.email, internalEmails))
    .map((ec) => {
      const isCompany = ec?.role === "client";
      // A person row matches contacts only and a company row clients only: a
      // client offered as a person's "Link" can't become the quote's contact
      // (the commit and the save check only know contacts), so the operator
      // would confirm it and then be refused at save (2026-10-26).
      const all = isCompany ? (clients || []) : (contacts || []);
      let match = null;
      let confidence = "low";

      // Tier 1 — email. Proof, for people and companies alike.
      const email = normalizeEmail(ec?.email);
      if (email) {
        match = all.find((r) => recordEmails(r).includes(email));
        if (match) confidence = "high";
      }

      // Tier 2 — org domain, COMPANY ROWS ONLY, against clients only. A domain
      // identifies an organization, never a person: suggesting a client record
      // as a *person's* match is a category error the commit step can't honor —
      // it would show "linked" in the UI and then silently create a fresh person
      // anyway (Gemini review 2026-08-26, G4). Generic mail hosts excluded.
      if (!match && isCompany) {
        const domain = normalizeDomain(ec?.url) || emailDomain(ec?.email);
        if (isOrgDomain(domain)) {
          match = (clients || []).find((r) => recordDomains(r).includes(domain));
          if (match) confidence = "high";
        }
      }

      // Tier 3 — exact name (legal suffixes stripped for companies).
      if (!match) {
        const key = isCompany ? normalizeCompanyName(ec?.name) : normalizeName(ec?.name);
        if (key) {
          match = all.find(
            (r) => (isCompany ? normalizeCompanyName(r.name) : normalizeName(r.name)) === key
          );
          if (match) confidence = "medium";
        }
      }

      // Tier 4 — token subset. ADVISORY ONLY: a human confirms this row before
      // anything is written. Never reachable from resolveContact/resolveClient.
      if (!match) {
        const parts = normalizeName(ec?.name).split(" ").filter(Boolean);
        if (parts.length >= 2) {
          match = all.find((r) => {
            const n = normalizeName(r.name);
            return n && parts.every((p) => n.includes(p));
          });
          if (match) confidence = "low";
        }
      }

      return { extracted: ec, match: match || null, _originalMatch: match || null, confidence };
    });
}
